// Keeps this device and the cloud copy of progress in step while signed in.
// Local changes are pushed (debounced); the server merges and returns the
// merged document, which is merged back in. Merging is idempotent, so
// retries and overlapping syncs can never lose or double anything.
import { api } from './api.js';
import { getProgress, adoptMerged, subscribe, local, setLocal } from './store.js';

const state = {
  user: null,
  status: 'checking', // checking | local | syncing | synced | offline | error
  lastSyncAt: local().lastSyncAt || null,
};
const listeners = new Set();
let timer = null;
let inflight = null;
let again = false;
let lastPull = 0;

export function syncState() {
  return state;
}

export function onSync(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function set(patch) {
  Object.assign(state, patch);
  for (const fn of listeners) fn(state);
}

function signedOut() {
  setLocal({ user: null });
  set({ user: null, status: 'local' });
}

function handleError(e) {
  if (e.status === 401) return signedOut();
  set({ status: e.code === 'offline' ? 'offline' : 'error' });
}

export async function initSync() {
  subscribe((doc, reason) => {
    if (reason !== 'sync' && state.user) schedule();
  });
  window.addEventListener('online', () => {
    if (state.user) pushNow();
    else refreshUser();
  });
  window.addEventListener('offline', () => {
    if (state.user) set({ status: 'offline' });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && state.user && Date.now() - lastPull > 30_000) pull();
  });
  // Show the cached account immediately, then confirm with the server.
  const cached = local().user;
  if (cached) set({ user: cached, status: navigator.onLine === false ? 'offline' : 'syncing' });
  await refreshUser();
}

export async function refreshUser() {
  try {
    const { user } = await api('/api/me');
    if (!user) return signedOut();
    setLocal({ user });
    set({ user, status: 'syncing' });
    await pull();
  } catch (e) {
    if (e.code === 'offline' && state.user) set({ status: 'offline' });
    else if (e.code === 'offline') set({ status: 'local' });
    else handleError(e);
  }
}

async function pull() {
  lastPull = Date.now();
  try {
    set({ status: 'syncing' });
    const remote = await api('/api/progress');
    adoptMerged(remote.doc);
    // Push whatever this device had that the server did not.
    await pushNow(true);
  } catch (e) {
    handleError(e);
  }
}

function schedule() {
  clearTimeout(timer);
  timer = setTimeout(() => pushNow(), 1200);
}

export async function pushNow(fromPull = false) {
  if (!state.user) return;
  if (inflight) {
    again = true;
    return inflight;
  }
  clearTimeout(timer);
  if (!fromPull) set({ status: 'syncing' });
  inflight = (async () => {
    try {
      const res = await api('/api/progress', { method: 'PUT', body: { doc: getProgress() } });
      adoptMerged(res.doc);
      const now = Date.now();
      setLocal({ lastSyncAt: now });
      set({ status: 'synced', lastSyncAt: now });
    } catch (e) {
      handleError(e);
    } finally {
      inflight = null;
      if (again) {
        again = false;
        schedule();
      }
    }
  })();
  return inflight;
}

// Email sign-in, step 1: send a six-digit code to this address.
export function requestCode(email) {
  return api('/api/auth/email/start', { method: 'POST', body: { email } });
}

// Step 2: the code signs in (creating the account the first time), then this
// device's progress and the account's are merged both ways.
export async function verifyCode(email, code) {
  const p = getProgress();
  const res = await api('/api/auth/email/verify', {
    method: 'POST',
    body: { email, code, name: (p.profile && p.profile.name) || '' },
  });
  setLocal({ user: res.user, lastEmail: email });
  set({ user: res.user, status: 'syncing' });
  await pull();
  return res;
}

export async function signOut() {
  try {
    await pushNow();
    await api('/api/auth/logout', { method: 'POST', body: {} });
  } finally {
    signedOut();
  }
}

export async function deleteAccount() {
  await api('/api/account', { method: 'DELETE', body: {} });
  signedOut();
}

export async function rename(name) {
  const { user } = await api('/api/me', { method: 'PATCH', body: { name } });
  setLocal({ user });
  set({ user });
}
